import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Notification } from 'libs/mona/api/src/lib/model';
import { Observable } from 'rxjs';
import { Encounter } from '@mona/models';
import { AppState, selectRouteParam } from '@mona/store';
import { NotificationsAction, NotificationsSelectors } from '../state';

/**
 * Notifications store service
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
    /**
     * Constructor
     *
     * @param store store
     * @param actions$
     */
    constructor(private store: Store<AppState>, private actions$: Actions) {}

    //#region Selectors

    /**
     * Get current encounter from route
     */
    getCurrentEncounterFromRoute(): Observable<string> {
        return this.store.select<any>(selectRouteParam('encounterId'));
    }

    /**
     * Get paginated notifications
     */
    getAllNotifications(): Observable<Notification.Model[]> {
        return this.store.select(NotificationsSelectors.selectAllNotifications);
    }

    /**
     * Get unread notifications
     */
    getUnreadNotifications(): Observable<Notification.Model[]> {
        return this.store.select(NotificationsSelectors.selectUnreadNotifications);
    }

    /**
     * Get next URL for pagination
     */
    getNextUrlAll(): Observable<string | null> {
        return this.store.select(NotificationsSelectors.selectNextUrlAll);
    }

    /**
     * Get next URL for unread notifications pagination
     */
    getNextUrlUnread(): Observable<string | null> {
        return this.store.select(NotificationsSelectors.selectNextUrlUnread);
    }

    /**
     * Get whether currently loading more notifications
     */
    getIsLoadingMoreNotifications(): Observable<boolean> {
        return this.store.select(NotificationsSelectors.selectIsLoadingMoreNotifications);
    }

    /**
     * Get notifications
     */
    getHasUnreadNotifications(): Observable<boolean> {
        return this.store.select(NotificationsSelectors.selectHasUnreadNotifications);
    }

    /**
     * Get unread count
     */
    getUnreadCount(): Observable<number> {
        return this.store.select(NotificationsSelectors.selectUnreadCount);
    }

    /**
     * Get encounter notifications count
     *
     * @param encounterId EntityId<Encounter>
     */
    getEncounterNotificationsCount(encounterId?: EntityId<Encounter>): Observable<number> {
        return this.store.select(NotificationsSelectors.selectUnreadNotificationsCount);
    }

    //#endregion Selectors

    //#region Actions

    /**
     * Triggers paginated notifications loading
     *
     * @param encounterId
     * @param url Optional URL for pagination (next/previous links)
     * @param readStatus
     */
    loadPageableNotifications(encounterId?: EntityId<Encounter>, url?: string, readStatus?: boolean) {
        this.store.dispatch(NotificationsAction.loadPageableNotificationsAction({ encounterId, url, readStatus }));
    }

    /**
     * Returns an observable that emits when pageable notifications are successfully loaded.
     */
    getLoadNotificationsSuccessAction(): Observable<
        ReturnType<typeof NotificationsAction.loadPageableNotificationsActionSuccess>
    > {
        return this.actions$.pipe(ofType(NotificationsAction.loadPageableNotificationsActionSuccess));
    }

    /**
     * Load more notifications (for "load more" button)
     *
     * @param url Next page URL
     * @param readStatus notifications with readStatus true or false
     */
    loadMoreNotifications(url: string, readStatus?: boolean) {
        this.store.dispatch(NotificationsAction.loadPageableNotificationsAction({ url, readStatus, isLoadMore: true }));
    }

    /**
     * Clear paginated notifications
     */
    clearPaginatedNotifications() {
        this.store.dispatch(NotificationsAction.clearPaginatedNotificationsAction());
    }

    /**
     * Triggers has-unread notifications loading
     *
     * @param encounterId EntityId<Encounter>
     */
    loadHasUnreadNotifications(encounterId: EntityId<Encounter>) {
        this.store.dispatch(NotificationsAction.loadHasUnreadNotificationsAction.action({ encounterId }));
    }

    /**
     * Clears has-unread notifications
     */
    clearHasUnreadNotifications(): void {
        this.store.dispatch(NotificationsAction.loadHasUnreadNotificationsAction.clearAction());
    }

    /**
     * Add/update notification
     *
     * @param notification Notification.Model
     */
    addUpdateNotification(notification: Notification.Model) {
        this.store.dispatch(NotificationsAction.addUpdateNotificationAction({ notification }));
    }

    //#endregion Actions

    /**
     * Changes the read status of a notification
     *
     * @param notification Notification.Model
     * @param readStatus boolean
     */
    changePagedNotificationReadStatus(notification: Notification.Model, readStatus: boolean): void {
        this.store.dispatch(NotificationsAction.changePagedNotificationReadStatusAction({ notification, readStatus }));
    }

    /**
     * Marks multiple notifications as read
     *
     * @param notificationIds string[]
     */
    markMultipleNotificationsAsRead(notificationIds: string[]): void {
        this.store.dispatch(NotificationsAction.markMultipleNotificationsAsReadAction({ notificationIds }));
    }
}
